import React, { useState, useEffect, useCallback, memo } from "react";
import { Table, Button, Input, message, Modal, Space, Tag, Typography, Select, Form } from "antd";
import { PlusOutlined, CopyOutlined, StopOutlined, RedoOutlined } from "@ant-design/icons";
import moment from "moment";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { getAllStaffTokenStatus, issueStaffToken, revokeStaffToken, reissueStaffToken } from "./workpulseApi";

const { Text } = Typography;
const { Option } = Select;

const staffDisplayName = (item) => `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unknown";

const IssueTokenModal = ({ visible, onClose, onIssued, unissuedStaff }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (visible) setSelectedId(null); }, [visible]);

  const handleIssue = async () => {
    if (!selectedId) {
      message.error("Please select a staff member first.");
      return;
    }
    setLoading(true);
    const { error, result } = await issueStaffToken(selectedId);
    setLoading(false);
    if (error) {
      message.error(result?.message || "Failed to issue token.");
      return;
    }
    message.success("Token issued.");
    onIssued();
    onClose();
  };

  return (
    <Modal title="Generate Staff Code" visible={visible} onCancel={onClose} onOk={handleIssue} confirmLoading={loading} destroyOnClose>
      <Form layout="vertical">
        <Form.Item label="Staff Member" required>
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="Select a staff member"
            style={{ width: "100%" }}
            value={selectedId}
            onChange={setSelectedId}
            notFoundContent={unissuedStaff.length === 0 ? "Every staff member already has a token" : undefined}
          >
            {unissuedStaff.map((s) => <Option key={s.user_id} value={s.user_id}>{staffDisplayName(s)}</Option>)}
          </Select>
        </Form.Item>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Generates a persistent 4-digit code this person uses on any paired tablet to check in and out.
        </Text>
      </Form>
    </Modal>
  );
};

const StaffTokens = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issueVisible, setIssueVisible] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { error, result } = await getAllStaffTokenStatus();
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const issuedRows = rows.filter((r) => r.token_id);
  const unissuedStaff = rows.filter((r) => !r.token_id);

  const handleRevoke = useCallback((row) => {
    let reason = "";
    Modal.confirm({
      title: "Revoke token",
      icon: <StopOutlined />,
      content: <Input.TextArea rows={2} placeholder="Reason (required) — e.g. lost, terminated, compromised" onChange={(e) => { reason = e.target.value; }} />,
      okText: "Revoke",
      okType: "danger",
      onOk: async () => {
        if (!reason.trim()) { message.error("A reason is required to revoke a token."); return Promise.reject(); }
        const { error, result } = await revokeStaffToken(row.token_id, reason.trim());
        if (error) { message.error(result?.message || "Failed to revoke token."); return Promise.reject(); }
        message.success("Token revoked.");
        load();
      },
    });
  }, [load]);

  const handleReissue = useCallback(async (row) => {
    const { error, result } = await reissueStaffToken(row.token_id, row.user_id);
    if (error) message.error(result?.message || "Failed to reissue token.");
    else { message.success("Token reissued."); load(); }
  }, [load]);

  const handleCopy = useCallback((code) => {
    navigator.clipboard?.writeText(code);
    message.success("Token copied to clipboard.");
  }, []);

  const columns = [
    { title: "Staff", key: "name", render: (_, r) => staffDisplayName(r) },
    {
      title: "Staff Code",
      dataIndex: "token_code",
      key: "token_code",
      render: (v) => <Tag style={{ fontFamily: "monospace", fontSize: 14, padding: "3px 10px" }}>{v}</Tag>,
    },
    {
      title: "Issued",
      dataIndex: "issued_date",
      key: "issued_date",
      render: (v) => (v ? moment(v).format(DATE_FORMAT_DD_MM_YYYY) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(row.token_code)}>Copy</Button>
          <Button size="small" icon={<RedoOutlined />} onClick={() => handleReissue(row)}>Reissue</Button>
          <Button size="small" danger icon={<StopOutlined />} onClick={() => handleRevoke(row)}>Revoke</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIssueVisible(true)}>
          Generate Staff Code
        </Button>
      </div>
      <Table
        rowKey="user_id"
        columns={columns}
        dataSource={issuedRows}
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No staff codes generated yet — click Generate Staff Code to set one up for a staff member." }}
      />
      <IssueTokenModal
        visible={issueVisible}
        unissuedStaff={unissuedStaff}
        onClose={() => setIssueVisible(false)}
        onIssued={load}
      />
    </div>
  );
};

export default memo(StaffTokens);
